import React, { useEffect, useState } from "react";
import "ol/ol.css";
import { Route } from "@/types/routes";
import { apiClient } from "@/api/apiClient";
import { MapRoute } from "@/components/MapRoute/MapRoute";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Save } from "lucide-react";
import { Switch } from "@/components/ui/switch";

export const AdminRoutes = () => {
    const [routes, setRoutes] = useState<Route[]>([]);
    const [newRouteData, setNewRouteData] = useState("");
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [expandedRoute, setExpandedRoute] = useState<string | null>(null);
    const [importData, setImportData] = useState<Record<string, string>>({});
    const [showAddForm, setShowAddForm] = useState(false);

    const fetchRoutes = async () => {
        try {
            const fetchedRoutes = await apiClient.getAllRoutes();
            setRoutes(fetchedRoutes);
            setErrors({});
        } catch (e) {
            console.error("Ошибка при загрузке маршрутов", e);
        }
    };

    useEffect(() => {
        fetchRoutes();
    }, []);

    const handleAddRoute = async () => {
        try {
            const result = await apiClient.createRoute(newRouteData);
            if (typeof result === 'string') {
                setErrors((prev) => ({ ...prev, new: result }));
            } else {
                await fetchRoutes();
                setNewRouteData("");
                setErrors((prev) => ({ ...prev, new: "" }));
                setShowAddForm(false);
            }
        } catch (e: any) {
            const text = await e.response?.text?.();
            setErrors((prev) => ({ ...prev, new: text || "Ошибка при создании маршрута" }));
        }
    };

    const handleUpdateRoute = async (route: Route) => {
        try {
            const result = await apiClient.updateRoute({
                id: route.id,
                name: route.name,
                active: route.active,
                color: route.color,
            });
            if (typeof result === 'string') {
                setErrors((prev) => ({ ...prev, [route.id]: result }));
            } else {
                await fetchRoutes();
                setErrors((prev) => ({ ...prev, [route.id]: "" }));
            }
        } catch (e: any) {
            const text = await e.response?.text?.();
            setErrors((prev) => ({ ...prev, [route.id]: text || "Ошибка при обновлении маршрута" }));
        }
    };

    const handleImportRoute = async (id: string) => {
        try {
            const result = await apiClient.importRoute(id, importData[id]);
            if (typeof result === 'string') {
                setErrors((prev) => ({ ...prev, [id]: result }));
            } else {
                await fetchRoutes();
                setErrors((prev) => ({ ...prev, [id]: "" }));
            }
        } catch (e: any) {
            const text = await e.response?.text?.();
            setErrors((prev) => ({ ...prev, [id]: text || "Ошибка при импорте маршрута" }));
        }
    };

    const toggleExpandedRoute = (id: string) => {
        setExpandedRoute((prev) => (prev === id ? null : id));
    };

    return (
        <div className="p-4">
            <h1 className="text-2xl mb-4">Маршруты</h1>

            <Button onClick={() => setShowAddForm(!showAddForm)} className="mb-4">
                {showAddForm ? "Скрыть форму добавления" : "Добавить маршрут"}
            </Button>

            {showAddForm && (
                <div className="mb-4 border p-4 rounded-md bg-gray-50">
                    {errors.new && <div className="text-red-500 mb-2">{errors.new}</div>}
                    <Textarea
                        value={newRouteData}
                        placeholder="Текстовый трек с https://www.gpsvisualizer.com/"
                        onChange={(e) => setNewRouteData(e.target.value)}
                        className="w-full h-24 p-2 border"
                    />
                    <Button onClick={handleAddRoute} className="mt-2">Отправить</Button>
                </div>
            )}

            <table className="w-full border-collapse border border-gray-300">
                <thead>
                <tr className="bg-gray-200">
                    <th className="border p-2">Название</th>
                    <th className="border p-2">Активность</th>
                    <th className="border p-2">Цвет</th>
                    <th className="border p-2">Действия</th>
                </tr>
                </thead>
                <tbody>
                {routes.map((route) => (
                    <>
                        <tr key={route.id} className="border">
                            <td className="border p-2">
                                <Input
                                    value={route.name}
                                    onChange={(e) => setRoutes(rs => rs.map(r => r.id === route.id ? { ...r, name: e.target.value } : r))}
                                />
                            </td>
                            <td className="border p-2 text-center">
                                <Switch
                                    checked={route.active}
                                    onCheckedChange={(checked) => setRoutes(rs => rs.map(r => r.id === route.id ? { ...r, active: checked } : r))}
                                />
                            </td>
                            <td className="border p-2">
                                <Input
                                    value={route.color}
                                    onChange={(e) => setRoutes(rs => rs.map(r => r.id === route.id ? { ...r, color: e.target.value } : r))}
                                />
                            </td>
                            <td className="border p-2 space-y-2 text-center">
                                {errors[route.id] && <div className="text-red-500 mb-1">{errors[route.id]}</div>}
                                <Button size="icon" onClick={() => handleUpdateRoute(route)} className="w-full">
                                    <Save size={16} /> Сохранить
                                </Button>
                                <details className="inline-block mt-2 w-full">
                                    <summary className="cursor-pointer">Импорт</summary>
                                    <Textarea
                                        value={importData[route.id] || ""}
                                        placeholder="Текстовый трек с https://www.gpsvisualizer.com/"
                                        onChange={(e) => setImportData(data => ({ ...data, [route.id]: e.target.value }))}
                                        className="w-full h-16 border p-1 mt-2"
                                    />
                                    <Button onClick={() => handleImportRoute(route.id)} className="mt-1 w-full">
                                        Отправить
                                    </Button>
                                </details>
                                <Button
                                    size="sm"
                                    onClick={() => toggleExpandedRoute(route.id)}
                                    variant="secondary"
                                    className="w-full"
                                >
                                    {expandedRoute === route.id ? "Скрыть карту" : "Посмотреть на карте"}
                                </Button>
                            </td>
                        </tr>
                        {expandedRoute === route.id && (
                            <tr key={`map-${route.id}`}>
                                <td colSpan={4} className="p-2 border bg-gray-100">
                                    <MapRoute points={route.points} color={route.color} />
                                </td>
                            </tr>
                        )}
                    </>
                ))}
                </tbody>
            </table>
        </div>
    );
};
